// Feature flags — single source of truth for gating incomplete features.
//
// Cited: `AGENTS.md` prime directive (resolve, never restate) — flip a flag by
// changing this file; do not fork the disabled copy across call sites.
//
// Rationale (why hard-coded, not env-driven):
//   • R20 (honest UI): unfinished capabilities must render as visibly disabled
//     with truthful copy explaining the deferral. Env-based flags risk a
//     misconfigured deploy silently enabling a broken CTA.
//   • DEC-48: disabled states are reviewable in PRs, greppable, and cannot
//     drift between environments. Flipping a flag is a code change that goes
//     through review.
//
// To ship a feature: flip its flag to `true` in the same PR that lands the
// backing infra (S3 env var, endpoint, etc.), re-probe end-to-end, and remove
// the disabled-copy branches at call sites in the follow-up cleanup PR.

export const FLAGS = Object.freeze({
  // E2/E4 — attachment upload paths.
  //
  // 🛑 PRECONDITIONS FOR FLIPPING THIS TO `true` (operator ruling 2026-08-03):
  //
  //   1. `S3_BUCKET_FILES` env var set on the deployed `bidit-dev-quotes`
  //      Lambda — CTO/infra owned per DEC-51. Verified via
  //      `aws lambda get-function-configuration --function-name bidit-dev-quotes`.
  //      Handler code returns `500 File storage not configured` today
  //      (verified live on ACV Co. 2026-08-02); this is the observable
  //      branch until the env var lands.
  //
  //   2. **✅ SATISFIED 2026-08-03 (commit `808378a`):** `createAttachment`
  //      is now transactional — `TransactWriteItems` puts the Attachment
  //      record AND the owner-record ref append atomically. If either
  //      fails, both fail; no orphan attachments, no leaked S3 objects.
  //      Verified live on ACV Co. 2026-08-03: real WO transaction
  //      returned 201, ref appeared on `job.work_orders[*].attachments[]`,
  //      reload-persist confirmed. Original operator ruling 2026-08-03 §2.
  //      DEF-146 (fetchAttachment Query-on-non-key bug) fixed in the
  //      same commit — confirmUpload/retry/delete now actually work.
  //
  // Do NOT flip this flag without both preconditions above met. AttachModal
  // itself is built + unit-tested; only the entry-point CTAs (`+ Add`,
  // `+ Upload`, `Upload Document`) are hidden behind this flag — flipping
  // to `true` exposes the write path that commit 2 makes atomic.
  ATTACHMENTS_UPLOAD: true,
});

// Read a flag. Prefer `isEnabled('...')` over reading FLAGS directly so a
// future lookup layer (per-tenant, per-user, remote-config) is a one-file
// change with no consumer diff.
export function isEnabled(flag) {
  return FLAGS[flag] === true;
}
